import type { ComponentProps } from 'react';
import React from 'react';

import { ButtonIcon, ButtonProps, ButtonPropsSizeType, ButtonWTip } from '../Button';
import { cn } from '../cn';
import { buttonHeightAndBorder } from '../common';
import { General } from '../StrokeIcons';
import InputTitle from './InputTitle';
import InputValue from './InputValue';

export type InputProps = Omit<ComponentProps<'input'>, 'size' | 'title'> & {
  size?: ButtonPropsSizeType;
  text?: ButtonProps['text'];
  icon?: React.JSX.Element;
  rIcon?: React.JSX.Element;
  error?: string;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  titleClassName?: string;
  noBorder?: boolean;
  ref?: React.Ref<HTMLInputElement>;
};

function Input({
  size,
  icon,
  rIcon,
  title,
  subtitle,
  error,
  className,
  type,
  text,
  disabled,
  titleClassName,
  noBorder,
  ref,
  ...props
}: InputProps) {
  const [_type, setType] = React.useState(type);
  const valueLength = typeof props.value === 'string' ? props.value.length : 0;

  return (
    <div
      className={cn(
        'inline-flex min-w-28 flex-col text-slate-600 dark:text-slate-100',
        { 'text-stone-400 dark:text-slate-700': disabled },
        {
          'gap-1': size === 'md' || size == null,
          'min-w-22 gap-0.5': size === 'sm',
          'min-w-18 gap-0': size === 'xs',
        },
        className,
      )}>
      {title != null && (
        <InputTitle error={error} size={size} className={titleClassName}>
          {title}
        </InputTitle>
      )}
      <div
        className={cn(
          'inline-flex overflow-hidden font-sans transition-colors',
          {
            'bg-white focus-within:bg-apricot-white-50': !disabled,
            'bg-stone-50': disabled,
            'dark:bg-charade-950 dark:focus-within:bg-charade-800': !disabled,
            'dark:bg-slate-950': disabled,
          },
          {
            border: !noBorder,
            'border-slate-200 dark:border-charade-700': !text,
            'border-fair-pink-300 dark:border-fair-pink-900': !text && error,
            'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-700 dark:focus-within:bg-fair-pink-900':
              error,
          },
          buttonHeightAndBorder(size),
        )}>
        {icon && <ButtonIcon size={size}>{icon}</ButtonIcon>}
        <InputValue
          {...props}
          disabled={disabled}
          size={size}
          error={error}
          ref={ref}
          type={type === 'password' ? _type : type}
        />

        {valueLength > 0 && type != 'color' && type != 'date' && type != 'number' && (
          <>
            <ButtonWTip
              size={size}
              icon={<General.XMark />}
              text
              type={error ? 'reset' : 'button'}
              className="-mb-[1px] -mt-[1px] rounded-none first:-ml-[1px] last:-mr-[1px]"
              onClick={() => props.onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)}
            />
            {type === 'password' && (
              <div
                className={cn('w-[1px] h-5/6 my-auto bg-red-500', {
                  'bg-slate-200 dark:bg-charade-700': !text,
                  'bg-fair-pink-300 dark:bg-fair-pink-900': !text && error,
                })}
              />
            )}
          </>
        )}

        {type === 'password' && (
          <ButtonWTip
            size={size}
            tooltip={_type === 'password' ? 'Show password' : 'Hide password'}
            icon={_type === 'password' ? <General.Eye /> : <General.EyeOff />}
            text
            type={error ? 'reset' : 'button'}
            className="-mb-[1px] -mt-[1px] rounded-none first:-ml-[1px] last:-mr-[1px]"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setType((type) => (type === 'password' ? 'text' : 'password'));
            }}
          />
        )}
        {rIcon && <ButtonIcon size={size}>{rIcon}</ButtonIcon>}
      </div>
      {subtitle && <InputTitle size={size}>{subtitle}</InputTitle>}
    </div>
  );
}

export default Input;
